import type { GgxTourDefinition } from './tour-types';

export const HOWTO_07: GgxTourDefinition = {
    key: '[HOWTO-07] Creating a challenger pipeline and comparing results',
    route: '/home',
    estimatedMinutes: 4,
    prerequisite: {
        target: '[test-id="home-page-cards"]',
        message: 'Open the home page, then start this tour.',
    },
    intro: {
        headline: 'Testing out Challenger Pipelines',
        subtitle:
            'Your current pipeline is the champion. A challenger is a copy you change in one ' +
            'deliberate way, then test against the champion with the same reports, so the ' +
            'numbers tell you which one is better. This tour shows you how that works on the ' +
            'conversational AI example.',
    },
    outro: {
        title: "That's a wrap!",
        subtitle:
            'You have seen how to copy an existing pipeline into a challenger, adjust its ' +
            'settings, prompts, models and logic, and compare the results against the ' +
            'champion so the better one wins on evidence rather than opinion. Try it on any ' +
            'of the example pipelines: change a model, swap a RAG, tweak a prompt, and see ' +
            'what moves the numbers. If prompts are where you want to improve accuracy, the ' +
            'Refining your prompt via Hillclimbing tour is the structured way to do that.',
        learned: [
            'A **challenger** is a copy of a pipeline with one thing changed.',
            'Change a **model, RAG or prompt**, then run the same evaluation.',
            'The **better one wins on evidence**, not opinion.',
        ],
    },
    chapters: [
        {
            name: 'The idea',
            blurb: 'What a challenger is and why it helps',
            icon: 'grid',
            steps: [2],
        },
        {
            name: 'Watch it done',
            blurb: 'A short walkthrough you can follow on any pipeline',
            icon: 'pulse',
            steps: [3],
        },
    ],
    steps: [
        {
            title: 'How a chatbot answers, in three steps',
            whatHappens:
                'next, a short video walks through copying a pipeline and comparing it with the ' +
                'original.',
            icon: 'grid',
            description:
                'A typical conversational AI pipeline does three things with every question. ' +
                'First it works out the <strong>intent</strong>: what is the customer actually ' +
                'asking? Then it <strong>fetches any data</strong> the answer needs, such as an ' +
                'account balance. Finally it <strong>writes the response</strong>. Each of those ' +
                'steps can be tuned, and that is where a challenger comes in. You take the ' +
                'pipeline you already trust, make a copy, change one thing about it, and run ' +
                'both against the same objective reports. If the copy scores better, it becomes ' +
                'the new champion. If not, you have learned something at no risk to the ' +
                'original.',
        },
        {
            title: 'Watch it done, then try it yourself',
            whatHappens:
                'the tour wraps up with a summary and a pointer to the hill-climbing tour.',
            icon: 'pulse',
            description:
                'This short video walks through personalizing a custom-built pipeline: opening ' +
                'an example, making a copy, changing its settings, prompt and model, and ' +
                'comparing the results with the original. Watch it once, then follow the same ' +
                'steps on any pipeline you like. Your changes only ever touch the copy.',
            video: {
                src: 'https://genguardx.ai/tours/howto-07-demo.mp4',
                title: 'Personalizing a Custom-Built Pipeline',
            },
        },
    ],
};
